import { Bill } from '@/models/bill/bill'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

import { Button } from '@/components/ui/button'
import { printBillPdf, useGeneratePDF } from '@/modules/bill/services/bill'

export type BillPdfHandle = {
  print: () => Promise<void>
  printRound: (roundNo: number) => Promise<void>
  selectRound?: (roundNo: number) => void
  getSelectedRound?: () => number | undefined
}

export type BillPDFProps = {
  data: Bill
  handlePrintBill: () => void
  /** งวดที่แสดงใน preview — ถ้ามีประวัติชำระจะใช้งวดล่าสุดเมื่อไม่ระบุ */
  paymentRoundNo?: number
  /** เมื่อ true ไม่แสดงปุ่มพิมพ์ด้านล่าง (ให้ parent เรียก ref.print แทน) */
  hideInlinePrintButton?: boolean
}

export const BillPDF = forwardRef<BillPdfHandle, BillPDFProps>(function BillPDF(
  { data, handlePrintBill, paymentRoundNo, hideInlinePrintButton = false },
  ref,
) {
  const printRef = useRef<HTMLDivElement>(null)
  const [pdfUrl, setPdfUrl] = useState<string>('')

  const previewRound =
    paymentRoundNo ??
    (data.payment_rounds?.length
      ? data.payment_rounds[data.payment_rounds.length - 1]?.round_no
      : undefined)

  const { data: pdfBlob } = useGeneratePDF(data.id, previewRound)

  useEffect(() => {
    if (pdfBlob instanceof Blob) {
      const url = URL.createObjectURL(pdfBlob)
      setPdfUrl(url)
      return () => {
        URL.revokeObjectURL(url)
      }
    }
  }, [pdfBlob])

  const generatePDFFromDom = useCallback(async () => {
    const element = printRef.current
    if (!element) return null

    const canvas = await html2canvas(element, {
      scale: 2,
      logging: false,
      useCORS: true,
    })

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    })

    const pdfWidth = pdf.internal.pageSize.getWidth()
    const pdfHeight = pdf.internal.pageSize.getHeight()
    const pxPerMm = canvas.width / pdfWidth
    const pageHeightPx = pdfHeight * pxPerMm
    const pageCount = Math.ceil(canvas.height / pageHeightPx)

    for (let i = 0; i < pageCount; i++) {
      const pageCanvas = document.createElement('canvas')
      pageCanvas.width = canvas.width
      pageCanvas.height = Math.min(pageHeightPx, canvas.height - i * pageHeightPx)
      const ctx = pageCanvas.getContext('2d')
      if (ctx) {
        ctx.drawImage(
          canvas,
          0,
          i * pageHeightPx,
          canvas.width,
          pageCanvas.height,
          0,
          0,
          canvas.width,
          pageCanvas.height,
        )
        const pageImgData = pageCanvas.toDataURL('image/png')
        if (i > 0) pdf.addPage()
        pdf.addImage(pageImgData, 'PNG', 0, 0, pdfWidth, pageCanvas.height / pxPerMm)
      }
    }
    return pdf
  }, [])

  const openPrint = useCallback(
    async (roundNo?: number) => {
      if (pdfBlob && pdfUrl && roundNo === previewRound) {
        const printWindow = window.open(pdfUrl, '_blank')
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print()
          }
        }
        handlePrintBill()
        return
      }
      if (roundNo != null) {
        await printBillPdf(data.id, roundNo)
        handlePrintBill()
        return
      }
      if (pdfBlob && pdfUrl) {
        const printWindow = window.open(pdfUrl, '_blank')
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print()
          }
        }
        handlePrintBill()
      } else {
        const pdf = await generatePDFFromDom()
        if (pdf) {
          const pdfBlobLocal = pdf.output('blob')
          const pdfUrlLocal = URL.createObjectURL(pdfBlobLocal)
          const printWindow = window.open(pdfUrlLocal, '_blank')
          if (printWindow) {
            printWindow.onload = () => {
              printWindow.print()
              URL.revokeObjectURL(pdfUrlLocal)
            }
          }
          handlePrintBill()
        }
      }
    },
    [data.id, generatePDFFromDom, handlePrintBill, pdfBlob, pdfUrl, previewRound],
  )

  const handlePrint = useCallback(async () => {
    await openPrint(previewRound)
  }, [openPrint, previewRound])

  useImperativeHandle(
    ref,
    () => ({
      print: () => handlePrint(),
      printRound: (roundNo: number) => openPrint(roundNo),
    }),
    [handlePrint, openPrint],
  )

  const roundLabel =
    previewRound != null ? ` (งวดที่ ${previewRound})` : ''

  return (
    <div className="flex w-full flex-col gap-3">
      {previewRound != null ? (
        <p className="text-center text-sm font-medium text-primary">
          ตัวอย่างใบเสร็จรับเงิน{roundLabel}
        </p>
      ) : null}
      {pdfUrl ? (
        <div className="w-full overflow-hidden rounded-xl border border-border bg-muted/15 shadow-sm">
          <iframe
            src={pdfUrl}
            title="PDF Preview"
            className="min-h-[min(72vh,780px)] w-full border-0"
          />
        </div>
      ) : (
        <div className="flex min-h-[200px] items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 px-4 py-12 text-center text-sm text-muted-foreground">
          กำลังโหลดตัวอย่าง PDF…
        </div>
      )}

      {!hideInlinePrintButton ? (
        <div className="flex justify-center">
          <Button type="button" variant="outline" onClick={() => void handlePrint()}>
            สั่งพิมพ์{roundLabel}
          </Button>
        </div>
      ) : null}

      <div ref={printRef} className="sr-only" aria-hidden />
    </div>
  )
})

BillPDF.displayName = 'BillPDF'
